import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import CockpitHero from "../../components/CockpitHero";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { motion } from "framer-motion";
import {
  CalendarRange,
  GraduationCap,
  ScrollText,
  Award,
  ArrowUpRight,
  MapPin,
  Flag,
  Trophy,
  Info,
} from "lucide-react";

export default function DelegateDashboard() {
  const { user } = useAuth();
  const [regs, setRegs] = useState([]);
  const [events, setEvents] = useState([]);
  const [papers, setPapers] = useState([]);
  const [certs, setCerts] = useState([]);
  const [passport, setPassport] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/registrations"),
      api.get("/events"),
      api.get("/papers"),
      api.get("/certificates"),
    ]).then(([r, e, p, c]) => {
      setRegs(r.data); setEvents(e.data); setPapers(p.data); setCerts(c.data);
    });
    api.get("/passport/me")
      .then((r) => setPassport(r.data))
      .catch(() => setPassport(null));
  }, []);

  const myEvents = regs.map((r) => {
    const ev = events.find((e) => e.id === r.event_id);
    return ev ? { ...ev, registration: r } : null;
  }).filter(Boolean);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Personal briefing header */}
        <CockpitHero
          testId="delegate-overview-hero"
          eyebrow="Delegate Portal · Personal briefing"
          title="Hello,"
          titleAccent={user?.name?.split(" ")[0]}
          titleTail="."
          description="Your conferences, courses, papers and certificates — assembled in one editorial briefing."
        />

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatTile icon={CalendarRange} label="Events" value={myEvents.length} />
          <StatTile icon={ScrollText} label="Papers" value={papers.length} />
          <StatTile icon={Award} label="Certificates" value={certs.length} />
          <StatTile icon={GraduationCap} label="Courses" value={1} />
        </div>

        {/* Podium Points strip — delegate progression metric (NOT Diploks).
            Diploks is the organiser's platform-credit currency and is never
            shown to delegates. */}
        <div
          data-testid="delegate-podium-points-strip"
          className="mt-6 relative overflow-hidden rounded-sm border border-brand-gold/30 bg-brand-navy-dark text-brand-champagne p-6 md:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
        >
          <div aria-hidden className="bg-noise absolute inset-0 pointer-events-none" />
          <div aria-hidden className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-brand-gold/12 blur-3xl" />
          <div className="relative flex items-center gap-4">
            <div className="h-12 w-12 rounded-sm bg-brand-gold/15 border border-brand-gold/40 flex items-center justify-center shadow-gold-soft">
              <Trophy className="h-6 w-6 text-brand-gold" />
            </div>
            <div>
              <p className="eyebrow text-brand-gold">
                Podium Points
              </p>
              <p
                className="mt-2 font-display text-4xl text-brand-champagne leading-none"
                data-testid="delegate-podium-points-value"
              >
                {passport?.points?.toLocaleString() ?? "0"}
              </p>
              <p className="mt-2 text-[11px] text-brand-champagne/55 flex items-start gap-1 max-w-xl">
                <Info className="h-3 w-3 mt-0.5 text-brand-gold shrink-0" />
                <span>Earn points by participating, debating and submitting papers. Podium Points are a personal achievement metric — not a wallet, not a currency.</span>
              </p>
            </div>
          </div>
          <div className="relative flex flex-col items-start md:items-end gap-2">
            {passport?.level?.name && (
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold bg-brand-gold text-brand-navy-dark rounded-sm px-3 py-1 shadow-gold-soft">
                <Trophy className="h-3 w-3" /> {passport.level.name}
              </span>
            )}
            <Link
              to="/dashboard/passport"
              data-testid="delegate-view-passport-link"
              className="text-xs text-brand-gold hover:underline inline-flex items-center gap-1 uppercase tracking-[0.18em] font-bold"
            >
              View passport <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
        </div>

        <div className="mt-10 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <p className="eyebrow text-brand-gold-dark">Your conferences</p>
            <h2 className="mt-1 font-display text-2xl text-brand-navy-dark">My events</h2>
            <div className="mt-4 space-y-4">
              {myEvents.map((ev) => (
                <Link
                  to={`/events/${ev.slug}`}
                  key={ev.id}
                  className="group block rounded-xl overflow-hidden border border-slate-200 bg-white hover:-translate-y-1 hover:shadow-md transition-all"
                  data-testid={`delegate-event-${ev.id}`}
                >
                  <div className="grid md:grid-cols-3">
                    <div className="h-40 md:h-full bg-cover bg-center" style={{ backgroundImage: `url(${ev.banner_url})` }} />
                    <div className="md:col-span-2 p-6">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                          {ev.format} · {ev.status}
                        </p>
                        <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-[#D4AF37]" />
                      </div>
                      <h3 className="mt-2 text-xl font-semibold tracking-tight text-[#0A192F]">{ev.title}</h3>
                      <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><CalendarRange className="h-3.5 w-3.5" />{new Date(ev.start_date).toLocaleDateString()}</span>
                        <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{ev.location}</span>
                        <span className="flex items-center gap-1"><Flag className="h-3.5 w-3.5" />{ev.registration?.payment_status}</span>
                      </div>
                      {ev.registration?.allocated_country_id && (
                        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0A192F] text-white text-xs font-semibold">
                          <span className="h-2 w-2 rounded-full bg-[#D4AF37]" />
                          Allocated
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
              {myEvents.length === 0 && (
                <Card className="bg-white border-slate-200 border-dashed">
                  <CardContent className="p-10 text-center">
                    <p className="text-slate-500 text-sm">You haven't registered for any events yet.</p>
                    <Link to="/dashboard/events"><Button className="mt-4 bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="browse-events-button">Browse events</Button></Link>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          <div className="lg:col-span-4 space-y-6">
            <Card className="bg-[#0A192F] text-white border-[#D4AF37]/30">
              <CardContent className="p-6">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                  Pre-MUN Primer
                </p>
                <h3 className="mt-2 text-xl font-semibold tracking-tight">Delegate's Playbook</h3>
                <p className="mt-2 text-sm text-slate-300">3 lessons · quiz · certificate</p>
                <Link to="/dashboard/courses">
                  <Button className="mt-5 bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold text-xs uppercase tracking-[0.18em] w-full" data-testid="start-course-button">
                    Start course
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200">
              <CardContent className="p-6">
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                  Quick actions
                </p>
                <div className="mt-4 space-y-2">
                  <Link to="/dashboard/papers" className="flex items-center justify-between p-3 rounded-md border border-slate-200 hover:border-[#D4AF37] transition-colors" data-testid="submit-paper-link">
                    <span className="text-sm font-semibold text-[#0A192F]">Submit position paper</span>
                    <ArrowUpRight className="h-4 w-4 text-[#0F4C81]" />
                  </Link>
                  <Link to="/dashboard/certificates" className="flex items-center justify-between p-3 rounded-md border border-slate-200 hover:border-[#D4AF37] transition-colors" data-testid="view-certs-link">
                    <span className="text-sm font-semibold text-[#0A192F]">My certificates</span>
                    <ArrowUpRight className="h-4 w-4 text-[#0F4C81]" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function StatTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5">
      <Icon className="h-4 w-4 text-[#0F4C81]" />
      <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
        {label}
      </p>
      <p className="mt-1 font-serif text-3xl text-[#0A192F]">{value}</p>
    </div>
  );
}
